import { Link } from 'react-router-dom'
import { useQueryState } from 'nuqs'
import { ChevronDown, ChevronUp, FileSignature, Phone, X } from 'lucide-react'
import { useDataTable } from '@/components/data-table/data-table-provider'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useSheetRowNavigation } from '@/hooks/useSheetRowNavigation'
import { useShellHref } from '@/utils/router'
import { StoredDocumentPreview } from './StoredDocumentPreview'
import { documentDate, type CollectedDocument } from './model'
import { SigningStatus } from './table'
import { DocumentDownload } from './DocumentDownload'
import { CancelDocument } from './CancelDocument'

function DocumentContent({
  document,
  activeTab,
  onTabChange,
}: {
  document: CollectedDocument
  activeTab: string
  onTabChange: (tab: string) => void
}) {
  const shellHref = useShellHref()
  const unavailable =
    document.state === 'failed' || document.state === 'preparing'
  return (
    <Tabs
      value={activeTab}
      onValueChange={onTabChange}
      className="flex min-h-0 flex-1 flex-col gap-0">
      <TabsList
        variant="line"
        className="w-full shrink-0 gap-7 border-b border-neutral-subtle-border px-4">
        <TabsTrigger
          value="document"
          className="cursor-pointer px-0.5 text-[13px]">
          Document
        </TabsTrigger>
        <TabsTrigger
          value="activity"
          className="cursor-pointer px-0.5 text-[13px]">
          Activity
        </TabsTrigger>
      </TabsList>
      <TabsContent
        value="document"
        className="mt-0 flex min-h-0 flex-1 flex-col">
        {!unavailable ? (
          <StoredDocumentPreview
            key={document.id}
            documentId={document.id}
            version={document.state}
          />
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center text-sm text-muted-foreground">
            <FileSignature className="size-7" />
            <p>
              {document.state === 'preparing'
                ? 'Preparing document…'
                : document.state === 'failed'
                  ? 'The document could not be prepared.'
                  : 'Document unavailable.'}
            </p>
          </div>
        )}
      </TabsContent>
      <TabsContent
        value="activity"
        className="mt-0 min-h-0 flex-1 overflow-auto px-4 py-4 [scrollbar-gutter:stable]">
        <ol className="space-y-6" aria-label="Document activity">
          {document.activity.toReversed().map((event, index) => (
            <li key={`${event.label}-${event.at}`} className="flex gap-3">
              <span
                className={`mt-1.5 size-2 shrink-0 rounded-full ${index === 0 ? 'bg-primary' : 'bg-muted-foreground/35'}`}
              />
              <div>
                <p className="text-sm font-medium">{event.label}</p>
                {event.reason && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    {event.reason}
                  </p>
                )}
                {event.callId && (
                  <Link
                    to={shellHref(
                      `/calls?${new URLSearchParams({ call_id: event.callId, details: '1' })}`
                    )}
                    className="my-1 flex w-fit items-center gap-1 text-sm text-primary hover:underline">
                    <Phone className="size-3.5" />
                    View call
                  </Link>
                )}
                <time
                  dateTime={event.at}
                  className="text-xs text-muted-foreground">
                  {new Date(event.at).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </time>
              </div>
            </li>
          ))}
        </ol>
      </TabsContent>
    </Tabs>
  )
}

export function SigningDocumentSheet() {
  const shellHref = useShellHref()
  const { table, rowSelection, isLoading } = useDataTable<
    CollectedDocument,
    unknown
  >()
  const {
    selectedRowKey,
    selectedRow,
    prevId,
    nextId,
    onPrev,
    onNext,
    onOpenChange,
  } = useSheetRowNavigation(table, rowSelection, !!isLoading)
  const [rawTab, setTab] = useQueryState('documentTab', { history: 'replace' })
  const activeTab = rawTab === 'activity' ? 'activity' : 'document'
  const handleOpenChange = (open: boolean) => {
    if (!open) void setTab(null)
    onOpenChange(open)
  }
  const document = selectedRow?.original
  return (
    <Sheet open={!!selectedRowKey} onOpenChange={handleOpenChange}>
      <SheetContent
        showCloseButton={false}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
        className="bg-neutral-ui-bg w-full max-w-none gap-0 rounded-l-xl border-l-0 p-0 sm:max-w-3xl">
        <SheetHeader className="shrink-0 gap-2 px-4 pb-1 pt-4">
          <div className="flex items-center gap-2">
            <FileSignature className="size-5 shrink-0 text-muted-foreground" />
            <SheetTitle className="min-w-0 flex-1 truncate text-lg">
              {document?.formName ?? 'Document'}
            </SheetTitle>
            <Button
              variant="ghost"
              size="icon-sm"
              className="cursor-pointer"
              aria-label="Previous document"
              disabled={!prevId}
              onClick={onPrev}>
              <ChevronUp />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              className="cursor-pointer"
              aria-label="Next document"
              disabled={!nextId}
              onClick={onNext}>
              <ChevronDown />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              className="cursor-pointer"
              aria-label="Close document"
              onClick={() => handleOpenChange(false)}>
              <X />
            </Button>
          </div>
          <SheetDescription className="flex flex-wrap items-center gap-x-2 gap-y-1 text-left text-sm">
            {document?.memberId ? (
              <Link
                to={shellHref(
                  `/data/patients?${new URLSearchParams({ 'patients.id': document.memberId, details: '1', clientTab: 'documents' })}`
                )}
                className="font-medium text-primary hover:underline">
                {document.memberName}
              </Link>
            ) : (
              <span className="font-medium text-foreground">
                {document?.memberName}
              </span>
            )}
            <span aria-hidden>·</span>
            {document?.configurationId ? (
              <Link
                to={shellHref(
                  `/tools/document-signing/${encodeURIComponent(document.formKey)}/${encodeURIComponent(document.configurationId)}`
                )}
                className="text-primary hover:underline">
                {document.configurationName}
              </Link>
            ) : (
              <span>{document?.configurationName}</span>
            )}
          </SheetDescription>
          {document && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <SigningStatus state={document.state} />
              <span className="flex-1 text-xs text-muted-foreground">
                {document.signedAt
                  ? `Signed ${documentDate(document.signedAt)}`
                  : `Created ${documentDate(document.createdAt)}`}
              </span>
              <CancelDocument
                key={`cancel-${document.id}-${document.state}`}
                document={document}
              />
              <DocumentDownload key={document.id} documentId={document.id} />
            </div>
          )}
        </SheetHeader>
        {document ? (
          <DocumentContent
            document={document}
            activeTab={activeTab}
            onTabChange={(tab) => void setTab(tab === 'activity' ? tab : null)}
          />
        ) : (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Loading document…
          </p>
        )}
      </SheetContent>
    </Sheet>
  )
}
